import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { FileText, Image, FileIcon, ExternalLink } from 'lucide-react';
import { Expense } from '../../../services/supabase/expenses.service';

interface UploadedFilesProps {
  expenses: Expense[];
  maxFiles?: number;
}

const getFileIcon = (url: string) => {
  const extension = url.split('.').pop()?.toLowerCase();

  if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(extension || '')) {
    return <Image className="h-4 w-4" />;
  } else if (extension === 'pdf') {
    return <FileText className="h-4 w-4" />;
  }
  return <FileIcon className="h-4 w-4" />;
};

const getFileName = (url: string, description: string): string => {
  // Try to extract filename from URL
  const urlParts = url.split('/');
  const filename = urlParts[urlParts.length - 1];

  // If we have a meaningful filename, use it
  if (filename && filename.length > 0 && filename !== 'undefined') {
    return decodeURIComponent(filename);
  }

  // Otherwise, use the expense description
  return description || 'Receipt';
};

const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
};

export const UploadedFiles: React.FC<UploadedFilesProps> = ({
  expenses,
  maxFiles = 5
}) => {
  // Get unique receipt URLs with associated expense data
  const uniqueFiles = expenses
    .filter(expense => expense.receipt_url)
    .reduce((acc, expense) => {
      const url = expense.receipt_url!;
      if (!acc.has(url)) {
        acc.set(url, expense);
      }
      return acc;
    }, new Map<string, Expense>());

  const uniqueFilesArray = Array.from(uniqueFiles.values()).slice(0, maxFiles);
  const totalUniqueFiles = uniqueFiles.size;

  if (uniqueFilesArray.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-sm font-medium">Uploaded Files</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <FileIcon className="h-12 w-12 text-muted-foreground mb-3" />
            <p className="text-sm text-muted-foreground">No receipts uploaded yet</p>
            <p className="text-xs text-muted-foreground mt-1">
              Upload receipts when creating expenses
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm font-medium">
          Uploaded Files ({totalUniqueFiles})
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {uniqueFilesArray.map((expense) => (
            <a
              key={expense.receipt_url}
              href={expense.receipt_url!}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-start gap-3 p-4 rounded-lg border bg-card hover:bg-muted/50 transition-colors"
            >
              <div className="flex-shrink-0 w-10 h-10 rounded-md bg-muted flex items-center justify-center">
                {getFileIcon(expense.receipt_url!)}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">
                  {getFileName(expense.receipt_url!, expense.description)}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  {expense.category}
                </p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {formatDate(expense.expense_date)}
                </p>
              </div>
              <ExternalLink className="flex-shrink-0 h-4 w-4 text-muted-foreground mt-1" />
            </a>
          ))}
        </div>

        {totalUniqueFiles > maxFiles && (
          <div className="mt-4 text-center">
            <p className="text-xs text-muted-foreground">
              +{totalUniqueFiles - maxFiles} more files
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
